import { useEffect, useCallback, useRef } from "react";
import { useSocket } from "./useSocket";

/**
 * 💬 CHAT-SPECIFIC SOCKET HOOK
 * Handles real-time messaging for chat interface
 * Based on backend Universal Socket Event Emitter
 */
export const useChatSocket = ({
  conversationId = null,
  channelId = null,
  onNewMessage = null,
  onAIResponse = null,
  onConversationUpdated = null,
  onConversationAssigned = null,
  onConversationResolved = null,
  onConversationDisabled = null,
} = {}) => {
  const { socket, isConnected } = useSocket();
  const handlersRef = useRef({
    onNewMessage,
    onAIResponse,
    onConversationUpdated,
    onConversationAssigned,
    onConversationResolved,
    onConversationDisabled,
  });

  // Update handlers ref when props change
  useEffect(() => {
    handlersRef.current = {
      onNewMessage,
      onAIResponse,
      onConversationUpdated,
      onConversationAssigned,
      onConversationResolved,
      onConversationDisabled,
    };
  }, [
    onNewMessage,
    onAIResponse,
    onConversationUpdated,
    onConversationAssigned,
    onConversationResolved,
    onConversationDisabled,
  ]);

  /**
   * 📨 Handle incoming chat events
   */
  const handleAppUpdate = useCallback(
    (event) => {
      const {
        type,
        data,
        conversationId: eventConversationId,
        channelId: eventChannelId,
      } = event;

      // Filter events by conversation/channel if specified
      if (
        conversationId &&
        eventConversationId &&
        eventConversationId !== conversationId
      ) {
        return;
      }

      if (channelId && eventChannelId && eventChannelId !== channelId) {
        return;
      }

      // Handle different event types
      switch (type) {
        case "new-message":
          if (handlersRef.current.onNewMessage) {
            // 🔄 COMPATIBILITY: Handle both backend formats with defensive programming
            const messageData = {
              id:
                data.messageId ||
                data.id ||
                `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              conversationId: eventConversationId,
              sender: {
                type: data.sender?.type || data.sender_type || "unknown",
                name: data.sender?.name || data.sender_name || "Unknown Sender",
              },
              content: data.content || data.message || "",
              timestamp:
                data.time ||
                data.timestamp ||
                new Date().toLocaleTimeString("id-ID", {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              date: data.date || new Date().toLocaleDateString("id-ID"),
              // 🔥 FIX: Direct media fields from backend (not nested in media object)
              is_media: data.is_media || false,
              media_type: data.media_type || "text",
              media_url: data.media_url || null,
              // 🔄 BACKWARD COMPATIBILITY: Keep nested media for legacy support
              media:
                data.is_media || data.media
                  ? {
                      type: data.media?.type || data.media_type || "unknown",
                      url: data.media?.url || data.media_url,
                      caption: data.media?.caption,
                    }
                  : null,
              delivery: data.delivery,
              cost: data.cost,
              processing_time: data.processing_time,
              tempId: data.tempId, // For optimistic message removal
            };

            // 🐛 DEBUG: Log parsed message data with validation
            console.log("📨 Parsed new-message:", {
              raw: data,
              parsed: messageData,
              conversationId: eventConversationId,
              hasRequiredFields: {
                id: !!messageData.id,
                content: !!messageData.content,
                timestamp: !!messageData.timestamp,
                senderType: !!messageData.sender.type,
              },
              // 🔥 NEW: Media debugging info
              mediaDebug: {
                rawIsMedia: data.is_media,
                rawMediaType: data.media_type,
                rawMediaUrl: data.media_url,
                parsedIsMedia: messageData.is_media,
                parsedMediaType: messageData.media_type,
                parsedMediaUrl: messageData.media_url,
                hasMedia: messageData.is_media || !!messageData.media_url,
              },
            });

            // 🛡️ VALIDATION: Only process if we have essential data
            if (
              messageData.id &&
              messageData.content &&
              messageData.sender.type
            ) {
              handlersRef.current.onNewMessage(messageData);
            } else {
              console.warn(
                "❌ Skipping message due to missing required fields:",
                {
                  missing: {
                    id: !messageData.id,
                    content: !messageData.content,
                    senderType: !messageData.sender.type,
                  },
                  rawData: data,
                }
              );
            }
          }
          break;

        case "ai-response":
          if (handlersRef.current.onAIResponse) {
            handlersRef.current.onAIResponse({
              messageId: data.messageId,
              message: data.message,
              cost: data.cost,
              model: data.model,
              action: data.action,
              confidence: data.confidence,
              processing_time: data.processing_time,
              escalation_reason: data.escalation_reason,
              // 🔥 FIX: Add media handling for AI responses
              hasMedia: data.hasMedia || !!data.media_url,
              media_url: data.media_url,
              media_type: data.hasMedia || data.media_url ? "image" : "text",
            });
          }
          break;

        case "conversation-updated":
          if (handlersRef.current.onConversationUpdated) {
            handlersRef.current.onConversationUpdated({
              conversationId: eventConversationId,
              status: data.status,
              escalation_reason: data.escalation_reason,
              customer_name: data.customer_name,
              last_message: data.last_message,
            });
          }
          break;

        case "conversation-assigned":
          if (handlersRef.current.onConversationAssigned) {
            handlersRef.current.onConversationAssigned({
              conversationId: eventConversationId,
              assignedTo: data.assignedTo,
              aiDisabled: data.aiDisabled,
              status: data.status,
              customer_name: data.customer_name,
            });
          }
          break;

        case "conversation-resolved":
          if (handlersRef.current.onConversationResolved) {
            handlersRef.current.onConversationResolved({
              conversationId: eventConversationId,
              resolvedBy: data.resolvedBy,
              backToAI: data.backToAI,
              status: data.status,
              customer_name: data.customer_name,
            });
          }
          break;

        case "conversation-disabled":
          if (handlersRef.current.onConversationDisabled) {
            handlersRef.current.onConversationDisabled({
              conversationId: eventConversationId,
              disabledBy: data.disabledBy,
              status: data.status || "disable",
              customer_name: data.customer_name,
            });
          }
          break;

        default:
          // Ignore other event types for chat
          break;
      }
    },
    [conversationId, channelId]
  );

  /**
   * 🔄 Subscribe to socket events
   */
  useEffect(() => {
    if (!socket || !isConnected) return;

    socket.on("app-update", handleAppUpdate);

    return () => {
      socket.off("app-update", handleAppUpdate);
    };
  }, [socket, isConnected, handleAppUpdate]);

  /**
   * 📊 Debug information
   */
  const getDebugInfo = useCallback(() => {
    return {
      isConnected,
      conversationId,
      channelId,
      socketId: socket?.id,
      hasHandlers: {
        onNewMessage: !!handlersRef.current.onNewMessage,
        onAIResponse: !!handlersRef.current.onAIResponse,
        onConversationUpdated: !!handlersRef.current.onConversationUpdated,
        onConversationAssigned: !!handlersRef.current.onConversationAssigned,
        onConversationResolved: !!handlersRef.current.onConversationResolved,
        onConversationDisabled: !!handlersRef.current.onConversationDisabled,
      },
    };
  }, [isConnected, conversationId, channelId, socket]);

  return {
    // Connection status
    isConnected,

    // Debug
    getDebugInfo,
  };
};
